/**
 * Utility functions for handling subcenter-related validation errors
 */

export interface SubcenterValidationError {
  type: 'MISSING_SUBCENTERS' | 'INACTIVE_SUBCENTERS' | 'WRONG_PARENT_CENTER' | 'SUBCENTERS_NOT_FOUND' | 'SUBCENTERS_WITH_SESSIONS' | 'GENERIC';
  message: string;
  details?: string[];
}

/**
 * Parse backend error messages to extract subcenter-specific validation errors
 */
export function parseSubcenterValidationError(errorMessage: string): SubcenterValidationError {
  // Check for missing subcenters error
  if (errorMessage.includes('Exam center must have at least one subcenter specified')) {
    return {
      type: 'MISSING_SUBCENTERS',
      message: 'Each exam center must have at least one sub-center selected.',
      details: ['Please select at least one sub-center for each exam center.']
    };
  }

  // Check for inactive subcenters error
  if (errorMessage.includes('Inactive or deleted subcenters:')) {
    const inactiveSubcenters = errorMessage.match(/Inactive or deleted subcenters: ([^.]+)/)?.[1];
    return {
      type: 'INACTIVE_SUBCENTERS',
      message: 'Some selected sub-centers are inactive or deleted.',
      details: inactiveSubcenters ? inactiveSubcenters.split(', ').map(s => s.trim()) : []
    };
  }

  // Check for wrong parent center error
  if (errorMessage.includes('Subcenters do not belong to specified center:')) {
    const wrongSubcenters = errorMessage.match(/Subcenters do not belong to specified center: ([^.]+)/)?.[1];
    return {
      type: 'WRONG_PARENT_CENTER',
      message: 'Some sub-centers do not belong to the selected exam center.',
      details: wrongSubcenters ? wrongSubcenters.split(', ').map(s => s.trim()) : []
    };
  }

  // Check for subcenters not found error
  if (errorMessage.includes('Subcenters not found:')) {
    const notFoundSubcenters = errorMessage.match(/Subcenters not found: \[([^\]]+)\]/)?.[1];
    return {
      type: 'SUBCENTERS_NOT_FOUND',
      message: 'Some selected sub-centers could not be found.',
      details: notFoundSubcenters ? notFoundSubcenters.split(', ').map(s => s.trim()) : []
    };
  }

  // Check for subcenters with active sessions error
  if (errorMessage.includes('Cannot remove the following subcenters because they have active exam sessions')) {
    const lines = errorMessage.split('\n').filter(line => line.trim().startsWith('-'));
    const subcentersWithSessions = lines.map(line => {
      const match = line.match(/- ([^:]+): (\d+) session\(s\)/);
      return match ? `${match[1]} (${match[2]} sessions)` : line.replace('- ', '');
    });
    
    return {
      type: 'SUBCENTERS_WITH_SESSIONS',
      message: 'Cannot remove sub-centers that have active exam sessions.',
      details: subcentersWithSessions
    };
  }

  // Generic error
  return {
    type: 'GENERIC',
    message: errorMessage
  };
}

/**
 * Get user-friendly error message for subcenter validation errors
 */
export function getSubcenterErrorMessage(error: SubcenterValidationError): string {
  switch (error.type) {
    case 'MISSING_SUBCENTERS':
      return 'Please select at least one sub-center for each exam center.';
    
    case 'INACTIVE_SUBCENTERS':
      return `The following sub-centers are inactive: ${error.details?.join(', ')}. Please select active sub-centers only.`;
    
    case 'WRONG_PARENT_CENTER':
      return `The following sub-centers don't belong to the selected exam center: ${error.details?.join(', ')}. Please check your selection.`;
    
    case 'SUBCENTERS_NOT_FOUND':
      return `The following sub-centers could not be found: ${error.details?.join(', ')}. Please refresh and try again.`;
    
    case 'SUBCENTERS_WITH_SESSIONS':
      return `Cannot remove sub-centers with active sessions:\n${error.details?.join('\n')}\n\nPlease move these sessions to other sub-centers first.`;
    
    default:
      return error.message;
  }
}

/**
 * Check if an error is related to subcenter validation
 */
export function isSubcenterValidationError(errorMessage: string): boolean {
  return [
    'Exam center must have at least one subcenter specified',
    'Inactive or deleted subcenters:',
    'Subcenters do not belong to specified center:',
    'Subcenters not found:',
    'Cannot remove the following subcenters because they have active exam sessions'
  ].some(pattern => errorMessage.includes(pattern));
}
